import {
    basicDenyPermission,
    basicUpdatePermission,
    basicViewPermission,
    type MakeReadOnly,
    type MedicalPermissionSection,
    nullPermission,
    PermissionHint,
    type PermissionSection,
    PermissionTitle,
    type Section,
    type TeamPermissions,
    teamDenyPermission,
    teamOwnerPermission,
    teamUpdatePermission,
    teamViewPermission,
} from "@qendo/permissions";
import { ComboBox, type ItemLike } from "~/components/form/combo-box/ComboBox";
import { SwitchField } from "~/components/form/SwitchField";
import { PermissionItem } from "~/components/layout/team/PermissionItem";
import { useProfile } from "~/providers/ProfileProvider";
import { isMedical, isTeam, usesMedical } from "~/util/qendo/team";

type EditMode = "share" | "edit";
type DisplayMode = EditMode | "display";

type SharePermissionOption = "off" | "view";
type BasicPermissionOption = SharePermissionOption | "update";
type MedicalPermissionOption = BasicPermissionOption | "medical";
type TeamPermissionOption = BasicPermissionOption | "assign_owner";

type ModePermissionSection<M extends EditMode, S extends Section> = [M] extends ["share"]
    ? MakeReadOnly<PermissionSection<S>>
    : PermissionSection<S>;

export type PermissionFieldProps<M extends DisplayMode, S extends Section> = {
    readonly section: S;
    value: PermissionSection<S>;
    mode?: M;
    setPermission: (section: S, permission: PermissionSection<S>) => void;
    label?: string | null;
    hint?: string | null;
    disabled?: boolean;
};

type PermissionOption<S extends Section> = S extends "team"
    ? TeamPermissionOption
    : S extends MedicalPermissionSection
      ? MedicalPermissionOption
      : BasicPermissionOption;

const BasicPermissionOptions = [
    { value: "off", label: "No access" },
    { value: "view", label: "View" },
    { value: "update", label: "Update" },
];
const MedicalPermissionOptions = [{ value: "medical", label: "As medical" }, ...BasicPermissionOptions];
const TeamPermissionOptions = [...BasicPermissionOptions, { value: "assign_owner", label: "Change owner" }];

function makePermission<M extends EditMode, S extends Section>(
    mode: M,
    section: S,
    option: PermissionOption<S> | null,
): PermissionSection<S> {
    if (mode === "edit") {
        switch (option) {
            case "assign_owner":
                return (isTeam(section) ? teamOwnerPermission : basicUpdatePermission) as ModePermissionSection<
                    "edit",
                    S
                >;
            case "update":
                return (isTeam(section) ? teamUpdatePermission : basicUpdatePermission) as ModePermissionSection<
                    "edit",
                    S
                >;
        }
    }

    switch (option) {
        case "view":
            return (isTeam(section) ? teamViewPermission : basicViewPermission) as PermissionSection<S>;
        case "off":
            return (isTeam(section) ? teamDenyPermission : basicDenyPermission) as PermissionSection<S>;
        default:
            return ((isMedical(section) && nullPermission) ||
                (isTeam(section) && teamDenyPermission) ||
                basicDenyPermission) as PermissionSection<S>;
    }
}

export const PermissionField = <M extends DisplayMode = DisplayMode, S extends Section = Section>({
    section,
    value,
    setPermission,
    label,
    hint,
    mode,
    ...fieldProps
}: PermissionFieldProps<M, S>) => {
    const { can } = useProfile();

    const field_hint = hint ?? PermissionHint[section];
    const permission_label = label ?? PermissionTitle[section];

    const selected_mode = mode ?? "edit";
    const team_mode = isTeam(section) && can("assign_owner", "team");
    const medical_mode = usesMedical(section, value);
    const edit_mode: EditMode | null =
        (selected_mode === "edit" && can("update", section) && "edit") ||
        (["edit", "share"].includes(selected_mode) && can("view", section) && "share") ||
        null;

    const display_mode: DisplayMode = edit_mode ?? "display";

    const combo_value = ((team_mode && (value as TeamPermissions).assign_owner && "assign_owner") ||
        (value.update && "update") ||
        (value.view && "view") ||
        (medical_mode && "medical") ||
        "off") as PermissionOption<S>;

    const switch_value = value.view ?? undefined;

    const combo_items =
        (team_mode && TeamPermissionOptions) || (medical_mode && MedicalPermissionOptions) || BasicPermissionOptions;

    const onChange = (option: PermissionOption<S> | null) => {
        if (edit_mode && setPermission) {
            setPermission(section, makePermission<typeof edit_mode, S>(edit_mode, section, option));
        }
    };

    switch (display_mode) {
        case "display": {
            return <PermissionItem section={section} title={permission_label} hint={field_hint} permission={value} />;
        }
        case "share": {
            return (
                <SwitchField
                    {...fieldProps}
                    label={permission_label}
                    hint={field_hint}
                    value={switch_value}
                    onValueChange={(view: boolean) => onChange((view ? "view" : "off") as PermissionOption<S>)}
                />
            );
        }
        default: {
            return (
                <ComboBox<PermissionOption<S>, ItemLike<PermissionOption<S>>>
                    {...fieldProps}
                    label={permission_label}
                    hint={field_hint}
                    items={combo_items}
                    value={combo_value}
                    onItemSelected={onChange}
                    searchable={false}
                />
            );
        }
    }
};
